// App mode (BON-561) — which of Nebari's two views a viewer is looking at.
//
// Mode replaced persona as the axis behind navigation, routing and page chrome.
// Persona was derived: role names collapsed to one of four values, and what
// each value saw was largely whatever survived filtering. Mode is granted:
// `access:admin_mode` and `access:teacher_mode` say, in the tenant config, what
// a role may look at.
//
// Entitlement is re-derived on every render and gates the stored choice, so a
// viewer who lost a grant since they last switched falls back rather than
// keeping a mode they can no longer enter. The stored value can only ever pick
// from what the viewer holds — it narrows, never widens.
//
// Persisted in localStorage (not sessionStorage, unlike `nebari-last-section`)
// so the chosen mode survives a reload and a new tab, the way a theme
// preference does.

import { Permission, type PermissionString } from '../../types/permissions';

export type ViewMode = 'admin' | 'teacher';

const STORAGE_KEY = 'nebari-view-mode';

/** The permission that admits a viewer to each mode. */
export const MODE_PERMISSION: Readonly<Record<ViewMode, PermissionString>> = {
  admin: Permission.ACCESS_ADMIN_MODE,
  teacher: Permission.ACCESS_TEACHER_MODE,
};

/** The route tree each mode owns. Every page lives under one of them, so the
 *  URL says which mode it belongs to and a deep link needs no other context. */
export const MODE_ROOT: Readonly<Record<ViewMode, string>> = {
  admin: '/admin',
  teacher: '/me',
};

/** Where each mode opens when the viewer has not asked for a page. */
export const MODE_HOME: Readonly<Record<ViewMode, string>> = {
  admin: '/admin/overview',
  teacher: '/me',
};

export function modeLabel(mode: ViewMode): string {
  return mode === 'admin' ? 'Admin' : 'Teacher';
}

/**
 * The modes a viewer may enter, in switcher order. EMPTY is a real answer.
 *
 * Both absent is a real state, not only a misconfiguration: a tab left open
 * across the deploy that adds these scopes holds a token minted before they
 * existed. This used to answer `['teacher']` for that viewer, as a "floor" —
 * which it was not. `RequireMode` gates `/me/*` on the very scope they are
 * missing, so the floor routed them to a tree that then refused them, and the
 * Unauthorized page's way home routed them back. Answering honestly is what
 * lets `HomeRedirect` say what happened instead.
 */
export function modesAvailableTo(
  hasPermission: (permission: PermissionString) => boolean,
): ViewMode[] {
  return (['admin', 'teacher'] as const).filter((mode) => hasPermission(MODE_PERMISSION[mode]));
}

/** The mode a pathname belongs to, or null for a route outside both trees.
 *  This is what makes a deep link switch modes: the URL is the authority while
 *  the viewer is on it, and the stored choice only decides where `/` opens. */
export function modeFromPath(pathname: string): ViewMode | null {
  for (const mode of ['admin', 'teacher'] as const) {
    const root = MODE_ROOT[mode];
    if (pathname === root || pathname.startsWith(`${root}/`)) return mode;
  }
  return null;
}

/** An admin workshop page, with or without its check-in modal open. */
const ADMIN_WORKSHOP_PATH = /^\/admin\/workshops\/([^/]+)(\/checkin)?\/?$/;

/**
 * The teacher-mode address of an admin page, or null when the page has no
 * teacher counterpart.
 *
 * Only the workshop page has one. A Regional Director who sends their crew the
 * workshop link sends the admin address, and without this the trainee or
 * assistant it was meant for lands on a refusal instead of the check-in.
 */
export function teacherPathFor(pathname: string): string | null {
  const match = ADMIN_WORKSHOP_PATH.exec(pathname);
  if (!match) return null;
  return `${MODE_ROOT.teacher}/workshops/${match[1]}${match[2] ?? ''}`;
}

function read(): ViewMode | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'admin' || raw === 'teacher' ? raw : null;
  } catch {
    // localStorage can be unavailable (incognito, blocked cookies). Falling
    // back to "no stored mode" just means the viewer lands on their default.
    return null;
  }
}

// Cached snapshot. useSyncExternalStore requires getSnapshot to be
// referentially stable between changes.
let snapshot: ViewMode | null = read();
const listeners = new Set<() => void>();

/** Subscribe to mode changes. Returns the unsubscribe function. */
export function subscribeViewMode(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getStoredViewMode(): ViewMode | null {
  return snapshot;
}

/** Remember the mode the viewer is in, or `null` to forget it. */
export function setStoredViewMode(next: ViewMode | null) {
  if (snapshot === next) return;
  snapshot = next;
  try {
    if (next) localStorage.setItem(STORAGE_KEY, next);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Persistence is best-effort; the in-memory snapshot still drives this
    // session so the switch works even when storage is blocked.
  }
  listeners.forEach((l) => l());
}

/** Test seam — reset the in-memory snapshot and the persisted value.
 *  Deliberately leaves `listeners` alone: clearing them would silently sever a
 *  mounted component's useSyncExternalStore subscription. */
export function resetViewModeForTests() {
  snapshot = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
